import 'core-js/stable';
import 'regenerator-runtime/runtime';
import { createApp, defineAsyncComponent } from 'vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faBars,
  faTimes,
  faChevronDown,
  faChevronUp,
  faSearch,
  faPlay,
} from '@fortawesome/free-solid-svg-icons';
import mitt from 'mitt';
import VuePlyr from 'vue-plyr';
import { initMenus } from './menus';
import { loadTermTranslations } from './utils/utils';
import { initDataTables } from './data-tables';
import {
  initBackToTop,
  backToTop,
  shiftTabFocusToTop,
  topButtonStickAboveFooter,
} from './back-to-top';
import termTranslations from '../../../../../sites/default/translations/mi.yml';

// Components
// ==========
// Caution: Those components are required on initial site load and cannot be lazy-loaded.
import MoeHeader from './components/MoeHeader/MoeHeader.vue';
import SiteSearchPage from './components/search/SiteSearchPage.vue';

// Lazy Loaded Components
// ======================
// Ref: https://learnvue.co/articles/lazy-load-components

const BannerSearch = defineAsyncComponent(
  () =>
    import(
      /* webpackChunkName: "BannerSearch" */
      './components/search/BannerSearch.vue'
    )
);
const SubjectBanner = defineAsyncComponent(
  () =>
    import(
      /* webpackChunkName: "SubjectBanner" */
      './components/SubjectBanner/SubjectBanner.vue'
    )
);
const HomepageBannerImage = defineAsyncComponent(
  () =>
    import(
      /* webpackChunkName: "HomepageBannerImage" */
      './components/HomepageBannerImage/HomepageBannerImage.vue'
    )
);
const ChangelogBanner = defineAsyncComponent(
  () =>
    import(
      /* webpackChunkName: "ChangelogBanner" */
      './components/ChangelogBanner/ChangelogBanner.vue'
    )
);
const ChangelogRecentChangesButton = defineAsyncComponent(
  () =>
    import(
      /* webpackChunkName: "ChangelogRecentChangesButton" */
      './components/ChangelogRecentChangesButton/ChangelogRecentChangesButton.vue'
    )
);
const ArongaBanner = defineAsyncComponent(
  () =>
    import(
      /* webpackChunkName: "ArongaBanner" */
      './components/ArongaBanner/ArongaBanner.vue'
    )
);
const AchievementStandardBanner = defineAsyncComponent(
  () =>
    import(
      /* webpackChunkName: "AchievementStandardBanner" */
      './components/AchievementStandardBanner/AchievementStandardBanner.vue'
    )
);
const InternalAssessmentActivityBanner = defineAsyncComponent(
  () =>
    import(
      /* webpackChunkName: "InternalAssessmentActivityBanner" */
      './components/InternalAssessmentActivityBanner/InternalAssessmentActivityBanner.vue'
    )
);
const StudentActivityRtfExport = defineAsyncComponent(
  () =>
    import(
      /* webpackChunkName: "StudentActivityRtfExport" */
      './components/StudentActivityRtfExport/StudentActivityRtfExport.vue'
    )
);
const AchievementStandardCriteria = defineAsyncComponent(
  () =>
    import(
      /* webpackChunkName: "AchievementStandardCriteria" */
      './components/AchievementStandardCriteria/AchievementStandardCriteria.vue'
    )
);
const NodeNavigator = defineAsyncComponent(
  () =>
    import(
      /* webpackChunkName: "NodeNavigator" */
      './components/Navigator/Navigator.vue'
    )
);
const Card = defineAsyncComponent(
  () =>
    import(
      /* webpackChunkName: "Card" */
      './components/Card/Card.vue'
    )
);
const AttachmentBlock = defineAsyncComponent(
  () =>
    import(
      /* webpackChunkName: "AttachmentBlock" */
      './components/AttachmentBlock/AttachmentBlock.vue'
    )
);
const MoeButton = defineAsyncComponent(
  () =>
    import(
      /* webpackChunkName: "MoeButton" */
      './components/MoeButton/MoeButton.vue'
    )
);
const ContentAccordion = defineAsyncComponent(
  () =>
    import(
      /* webpackChunkName: "ContentAccordion" */
      './components/ContentAccordion/ContentAccordion.vue'
    )
);
const FeedbackBannerAndModal = defineAsyncComponent(
  () =>
    import(
      /* webpackChunkName: "FeedbackBannerAndModal" */
      './components/FeedbackBannerAndModal/FeedbackBannerAndModal.vue'
    )
);
const ImageBlock = defineAsyncComponent(
  () =>
    import(
      /* webpackChunkName: "ImageBlock" */
      './components/ImageBlock/ImageBlock.vue'
    )
);
const SubjectGlossary = defineAsyncComponent(
  () =>
    import(
      /* webpackChunkName: "SubjectGlossary" */
      './components/subject-glossary/SubjectGlossary.vue'
    )
);
const GlossaryLink = defineAsyncComponent(
  () =>
    import(
      /* webpackChunkName: "GlossaryLink" */
      './components/GlossaryLink/GlossaryLink.vue'
    )
);
const AudioBlock = defineAsyncComponent(
  () =>
    import(
      /* webpackChunkName: "AudioBlock" */
      './components/AudioBlock/AudioBlock.vue'
    )
);
const FileCollectionDownloader = defineAsyncComponent(
  () =>
    import(
      /* webpackChunkName: "FileCollectionDownloader" */
      './components/FileCollectionDownloader/FileCollectionDownloader.vue'
    )
);
const Accordion = defineAsyncComponent(
  () =>
    import(
      /* webpackChunkName: "Accordion" */
      './components/Accordion/Accordion.vue'
    )
);
const AssessmentBanner = defineAsyncComponent(
  () =>
    import(
      /* webpackChunkName: "AssessmentBanner" */
      './components/AssessmentBanner/AssessmentBanner.vue'
    )
);
const PastScheduleAccordion = defineAsyncComponent(
  () =>
    import(
      /* webpackChunkName: "PastScheduleAccordion" */
      './components/PastScheduleAccordion/PastScheduleAccordion.vue'
    )
);
const VideoBlock = defineAsyncComponent(
  () =>
    import(
      /* webpackChunkName: "VideoBlock" */
      './components/VideoBlock/VideoBlock.vue'
    )
);

const emitter = mitt();

library.add(faBars);
library.add(faTimes);
library.add(faChevronDown);
library.add(faChevronUp);
library.add(faSearch);
library.add(faPlay);

const app = createApp({
  components: {
    MoeHeader,
    SiteSearchPage,
    BannerSearch,
    SubjectBanner,
    HomepageBannerImage,
    ChangelogBanner,
    ChangelogRecentChangesButton,
    ArongaBanner,
    AchievementStandardBanner,
    AchievementStandardCriteria,
    InternalAssessmentActivityBanner,
    StudentActivityRtfExport,
    NodeNavigator,
    Card,
    AttachmentBlock,
    MoeButton,
    ContentAccordion,
    FeedbackBannerAndModal,
    VideoBlock,
    ImageBlock,
    SubjectGlossary,
    GlossaryLink,
    AudioBlock,
    FileCollectionDownloader,
    Accordion,
    AssessmentBanner,
    PastScheduleAccordion,
  },
  data() {
    return {
      onPrint: null,
    };
  },
  created() {
    loadTermTranslations(termTranslations);

    // Fires a GTM event when the page is printed
    this.onPrint = (mql) => {
      if (mql.matches) {
        if (window.dataLayer) {
          window.dataLayer.push({
            event: 'PagePrint',
          });
        }
      }
    };
    window.matchMedia('print').addEventListener('change', this.onPrint);
  },
  // beforeDestroy deprecated in Vue3

  beforeDestroy() {
    // Removes the event listener when app is destroyed
    window.matchMedia('print').removeEventListener('change', this.onPrint);
  },
  mounted() {
    initBackToTop();
    initMenus();
    initDataTables();
    backToTop();
    topButtonStickAboveFooter();
    shiftTabFocusToTop();
  },
  methods: {
    initMenus,
    fileDownloadAnalytics(label) {
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'FileDownload',
          GALabel: `${label} - ${window.location.pathname}`,
        });
      }
    },
  },
});

app.config.globalProperties.emitter = emitter;

app.directive('visible', (el, binding) => {
  const element = el;
  element.style.visibility = binding.value ? 'visible' : 'hidden';
});

app.use(VuePlyr, {
  plyr: {
    settings: [],
    iconUrl: '/themes/custom/ncea/dist/images/vue-plyr-icons/vue-plyr-sprite.svg',

    // Override Plyr defaults.
    // https://github.com/sampotts/plyr#getters-and-setters
    controls: [
      'play-large', // The large play button in the center
      'rewind', // Rewind by the seek time (default 10 seconds)
      'play', // Play/pause playback
      'fast-forward', // Fast forward by the seek time (default 10 seconds)
      'progress', // The progress bar and scrubber for playback and buffering
      'current-time', // The current time of playback
      'duration', // The full duration of the media
      'mute', // Toggle mute
      'volume', // Volume control
      'captions', // Toggle captions
      'fullscreen', // Toggle fullscreen
    ],
  },
});

app.component('font-awesome-icon', FontAwesomeIcon);

app.mount('#app');
